import { Component, inject, Input, Output, EventEmitter } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../../../core/auth/auth.service';
import { Role } from '../../../../core/models/role.enum';

interface MenuItem {
  label: string;
  icon: string;
  route: string;
  permission?: string;
  backendPermission?: string;
  anyPermissions?: string[];
  allPermissions?: string[];
  roles?: Role[];
}

@Component({
  selector: 'app-sidebar',
  imports: [RouterLink, RouterLinkActive, TranslateModule],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
})
export class Sidebar {
  private auth = inject(AuthService);

  @Input() open = false;
  @Output() closed = new EventEmitter<void>();

  menuItems: MenuItem[] = [
    { label: 'nav.dashboard', icon: 'pi pi-home', route: '/dashboard' },
    { label: 'nav.stats', icon: 'pi pi-chart-bar', route: '/stats' },
    { label: 'nav.users', icon: 'pi pi-users', route: '/passengers', permission: 'users.view' },
    { label: 'nav.drivers', icon: 'pi pi-car', route: '/drivers', permission: 'users.view' },
    { label: 'nav.trips', icon: 'pi pi-map', route: '/trips', permission: 'trips.view' },
    { label: 'nav.bookings', icon: 'pi pi-calendar', route: '/bookings', permission: 'trips.view' },
    { label: 'nav.serviceRequests', icon: 'pi pi-inbox', route: '/service-requests' },
    { label: 'nav.activity', icon: 'pi pi-history', route: '/activity' },
    { label: 'nav.ratings', icon: 'pi pi-star', route: '/ratings', allPermissions: ['DRIVERS_READ', 'PASSENGERS_READ'] },
    { label: 'nav.compliance', icon: 'pi pi-verified', route: '/compliance', backendPermission: 'COMPLIANCE_READ' },
    { label: 'nav.tracking', icon: 'pi pi-bolt', route: '/tracking' },
    { label: 'nav.dispatch', icon: 'pi pi-sliders-h', route: '/dispatch' },
    { label: 'nav.geofencing', icon: 'pi pi-map-marker', route: '/geofencing', anyPermissions: ['GEOFENCE_READ', 'AIRPORT_READ'] },
    { label: 'nav.payments', icon: 'pi pi-credit-card', route: '/payments', roles: [Role.ADMIN] },
    { label: 'nav.outstandingBalances', icon: 'pi pi-exclamation-circle', route: '/payments/balances', backendPermission: 'BILLING_HISTORY_READ' },
    { label: 'nav.subscriptions', icon: 'pi pi-sync', route: '/subscriptions', allPermissions: ['DRIVERS_READ', 'DRIVERS_BILLING_READ'] },
    { label: 'nav.fees', icon: 'pi pi-dollar', route: '/fees', roles: [Role.ADMIN] },
    {
      label: 'nav.paymentConfig',
      icon: 'pi pi-sliders-v',
      route: '/fees/commission',
      backendPermission: 'PAYMENT_CONFIG_READ',
    },
    { label: 'nav.categories', icon: 'pi pi-th-large', route: '/categories', roles: [Role.ADMIN] },
    { label: 'nav.geo', icon: 'pi pi-globe', route: '/geo', roles: [Role.ADMIN] },
    { label: 'nav.promos', icon: 'pi pi-tag', route: '/promos', roles: [Role.ADMIN] },
    { label: 'nav.employers', icon: 'pi pi-id-card', route: '/employees', roles: [Role.ADMIN] },
    { label: 'nav.vendors', icon: 'pi pi-building', route: '/vendors', roles: [Role.ADMIN] },
    { label: 'nav.payouts', icon: 'pi pi-wallet', route: '/payouts', roles: [Role.ADMIN] },
    { label: 'nav.gatekeeper', icon: 'pi pi-shield', route: '/gatekeeper', backendPermission: 'AIRPORT_READ' },
    { label: 'nav.airports', icon: 'pi pi-building', route: '/airports', backendPermission: 'AIRPORT_READ' },
    { label: 'nav.team', icon: 'pi pi-users', route: '/team', roles: [Role.ADMIN] },
    { label: 'nav.notifications', icon: 'pi pi-envelope', route: '/notifications' },
    { label: 'nav.settings', icon: 'pi pi-cog', route: '/settings', roles: [Role.ADMIN, Role.TECH] },
    { label: 'nav.monitoring', icon: 'pi pi-chart-line', route: '/monitoring', backendPermission: 'MONITORING_VIEW' },
  ];

  get visibleItems(): MenuItem[] {
    return this.menuItems.filter((item) => {
      if (item.roles && !this.auth.hasAnyRole(...item.roles)) return false;
      if (item.allPermissions && !item.allPermissions.every(code => this.auth.hasPerm(code))) return false;
      if (item.anyPermissions && !item.anyPermissions.some(code => this.auth.hasPerm(code) || this.auth.hasPermission(code))) return false;
      if (item.permission && !this.auth.hasPermission(item.permission)) return false;
      if (item.backendPermission && !this.auth.hasPerm(item.backendPermission) && !this.auth.hasPermission(item.backendPermission)) return false;
      return true;
    });
  }

  /**
   * `routerLinkActive` matches by prefix, so a parent entry stays highlighted
   * while one of its children is open — `/fees` would light up alongside
   * `/fees/commission`, showing two active items at once.
   *
   * Exact matching is only correct for entries that actually have another menu
   * entry beneath them. Applying it everywhere would break entries whose own
   * detail pages are children — `/drivers/123` must keep Drivers active — so it
   * is derived from the menu rather than hardcoded, and a future nested route
   * gets the right behaviour without anyone remembering this.
   */
  isParentOfAnotherItem(item: MenuItem): boolean {
    return this.menuItems.some(
      (other) => other !== item && other.route.startsWith(`${item.route}/`),
    );
  }
}
